import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as countryCodes from 'country-codes-list';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useRbSystemAlert } from '@scmlatam/web-ui';
import type { SelectOption, UseFormUpdateEmployee, useFormUpdateEmployeeProps, ValidationErrors, EmployeeInitialValues } from '../types/hooks';
import { employeeService } from '../services/service';
import type { Alert, ParamsPatchEmployee } from '../types/services';
import { getUtcOffsetLabel } from '../utils/hooks';
import { usePermisoEmpleado as usePermiso } from '../../../../auth/permisosModulos';

const useFormUpdateEmployee = ({ rowsSelected, reload, option, setOption }: useFormUpdateEmployeeProps): UseFormUpdateEmployee => {
    const employeeId = rowsSelected[0]?.id ?? null;
    const { t } = useTranslation('workers.form.edit_worker');
    const { showAlert, closeAlert } = useRbSystemAlert();
    const canUpdateEmployee = usePermiso('editar-empleado');

    const title: string = t('edit_worker.title');
    const [openForm, setOpenForm] = useState<boolean>(false);

    const [initialValues, setInitialValues] = useState<EmployeeInitialValues>({
        first_name: '',
        last_name: '',
        document_type_id: '',
        document_employee: '',
        email: '',
        address: '',
        country_code: '',
        phone_number: '',
        birth_date: null,
        timezone: '',
    });

    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [documentTypes, setDocumentTypes] = useState<SelectOption[]>([]);
    const [documentType, setDocumentType] = useState<string>('');
    const [documentNumber, setDocumentNumber] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [address, setAddress] = useState<string>('');

    const cc = countryCodes.customList('countryCode', '+{countryCallingCode}');
    const [codes, setCodes] = useState<SelectOption[]>(
        Object.keys(cc).map((key) => ({
            value: key,
            label: key,
            sublabel: cc[key],
        })),
    );
    const [code, setCode] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [birthdate, setBirthdate] = useState<Dayjs | null>(null);

    const [timeZones, setTimeZones] = useState<SelectOption[]>(() =>
        Intl.supportedValuesOf('timeZone').map((tz) => ({
            value: tz,
            label: tz,
            sublabel: getUtcOffsetLabel(tz),
        })),
    );
    const [timeZone, setTimeZone] = useState<string>('');

    const [loadingForm, setLoadingForm] = useState<boolean>(false);
    const [loadingUpdateEmployee, setLoadingUpdateEmployee] = useState<boolean>(false);
    const [showMessage, setShowMessage] = useState<boolean>(false);

    const [searchDocumentType, setSearchDocumentType] = useState<string>('');
    const [searchTimeZone, setSearchTimeZone] = useState<string>('');
    const [searchCode, setSearchCode] = useState<string>('');

    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
        firstName: { error: false, message: '' },
        lastName: { error: false, message: '' },
        documentType: { error: false, message: '' },
        documentNumber: { error: false, message: '' },
        email: { error: false, message: '' },
        address: { error: false, message: '' },
        code: { error: false, message: '' },
        phoneNumber: { error: false, message: '' },
        birthdate: { error: false, message: '' },
        payRule: { error: false, message: '' },
        timeZone: { error: false, message: '' },
    });

    const checkInvalidData = (): boolean => {
        let hasErrors = false;

        if (firstName.trim() === '') {
            setValidationErrors((prev) => ({
                ...prev,
                firstName: { error: true, message: t('edit_worker.helper.first_name') },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                firstName: { error: false, message: '' },
            }));
        }

        if (lastName.trim() === '') {
            setValidationErrors((prev) => ({
                ...prev,
                lastName: { error: true, message: t('edit_worker.helper.last_name') },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                lastName: { error: false, message: '' },
            }));
        }

        if (documentType === '') {
            setValidationErrors((prev) => ({
                ...prev,
                documentType: { error: true, message: t('edit_worker.helper.document_type') },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                documentType: { error: false, message: '' },
            }));
        }

        if (documentNumber.trim() === '') {
            setValidationErrors((prev) => ({
                ...prev,
                documentNumber: { error: true, message: t('edit_worker.helper.document_number_1') },
            }));
            hasErrors = true;
        } else if (!new RegExp(documentTypes.find((dt) => dt.value === documentType)?.regex || '').test(documentNumber)) {
            setValidationErrors((prev) => ({
                ...prev,
                documentNumber: {
                    error: true,
                    message: t('edit_worker.helper.document_number_2', {
                        regex: documentTypes.find((dt) => dt.value === documentType)?.regex,
                    }),
                },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                documentNumber: { error: false, message: '' },
            }));
        }

        if (email.trim() === '') {
            setValidationErrors((prev) => ({
                ...prev,
                email: { error: true, message: t('edit_worker.helper.email_1') },
            }));
            hasErrors = true;
        } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
            setValidationErrors((prev) => ({
                ...prev,
                email: { error: true, message: t('edit_worker.helper.email_2') },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                email: { error: false, message: '' },
            }));
        }

        if (address.trim() === '') {
            setValidationErrors((prev) => ({
                ...prev,
                address: { error: true, message: t('edit_worker.helper.address') },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                address: { error: false, message: '' },
            }));
        }

        if (code.trim() === '') {
            setValidationErrors((prev) => ({
                ...prev,
                code: { error: true, message: t('edit_worker.helper.country_code') },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                code: { error: false, message: '' },
            }));
        }

        if (phoneNumber.trim() === '') {
            setValidationErrors((prev) => ({
                ...prev,
                phoneNumber: { error: true, message: t('edit_worker.helper.phone_1') },
            }));
            hasErrors = true;
        } else if (!/^9\d{4}\d{4}$/.test(phoneNumber)) {
            setValidationErrors((prev) => ({
                ...prev,
                phoneNumber: {
                    error: true,
                    message: t('edit_worker.helper.phone_2', { regex: '^9\\d{4}\\d{4}$' }),
                },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                phoneNumber: { error: false, message: '' },
            }));
        }

        if (birthdate === null) {
            setValidationErrors((prev) => ({
                ...prev,
                birthdate: { error: true, message: t('edit_worker.helper.birthdate') },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                birthdate: { error: false, message: '' },
            }));
        }

        if (timeZone === '') {
            setValidationErrors((prev) => ({
                ...prev,
                timeZone: { error: true, message: t('edit_worker.helper.timezone') },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                timeZone: { error: false, message: '' },
            }));
        }

        return hasErrors;
    };

    const hasChanges = (): boolean => {
        const fullPhoneNumber = code + phoneNumber;

        return (
            firstName !== initialValues.first_name ||
            lastName !== initialValues.last_name ||
            documentType !== initialValues.document_type_id ||
            documentNumber !== initialValues.document_employee ||
            email !== initialValues.email ||
            address !== initialValues.address ||
            fullPhoneNumber !== initialValues.phone_number ||
            birthdate?.format('YYYY-MM-DD') !== initialValues.birth_date?.format('YYYY-MM-DD') ||
            timeZone !== initialValues.timezone
        );
    };

    const getData = async () => {
        if (!employeeId) return;

        setLoadingForm(true);

        const { documentTypes: dts } = await employeeService.getDocumentTypes();
        setDocumentTypes(dts);

        const { employee } = await employeeService.getEmployeeById({ employeeId });

        if (employee) {
            const phoneMatch = employee.phone_number.match(/^([A-Z]{2})(.*)$/);
            const countryCode = phoneMatch ? phoneMatch[1] : '';
            const phone = phoneMatch ? phoneMatch[2] : employee.phone_number;

            const birthDate = employee.birth_date ? dayjs(employee.birth_date) : null;

            const initial: EmployeeInitialValues = {
                first_name: employee.first_name,
                last_name: employee.last_name,
                document_type_id: String(employee.document_type_id),
                document_employee: employee.document_employee,
                email: employee.email,
                address: employee.address,
                country_code: countryCode,
                phone_number: employee.phone_number,
                birth_date: birthDate,
                timezone: employee.timezone,
            };
            setInitialValues(initial);

            setFirstName(employee.first_name);
            setLastName(employee.last_name);
            setDocumentType(String(employee.document_type_id));
            setDocumentNumber(employee.document_employee);
            setEmail(employee.email);
            setAddress(employee.address);
            setCode(countryCode);
            setPhoneNumber(phone);
            setBirthdate(birthDate);
            setTimeZone(employee.timezone);
        }

        setLoadingForm(false);
    };

    const updateEmployee = async () => {
        if (!employeeId || !canUpdateEmployee) return;

        const params: ParamsPatchEmployee = {
            setLoading: setLoadingUpdateEmployee,
            employeeId,
            body: {
                first_name: firstName,
                last_name: lastName,
                birth_date: birthdate?.format('YYYY-MM-DD') || '',
                email: email,
                phone_number: code + phoneNumber,
                address: address,
                document_type_id: parseInt(documentType),
                document_employee: documentNumber,
                timezone: timeZone,
            },
            closing: () => {
                setOpenForm(false);
                setOption(null);
            },
            reloading: () => {},
        };

        const alert: Alert = {
            t: t,
            showAlert: (severity, message) => String(showAlert(severity, message)),
            closeAlert: closeAlert,
        };

        await employeeService.updateEmployee({ params, alert });
        await reload();
    };

    useEffect(() => {
        if (openForm) {
            getData();
        } else {
            setFirstName('');
            setLastName('');
            setDocumentType('');
            setDocumentNumber('');
            setEmail('');
            setAddress('');
            setCode('');
            setPhoneNumber('');
            setBirthdate(null);
            setTimeZone('');
            setValidationErrors({
                firstName: { error: false, message: '' },
                lastName: { error: false, message: '' },
                documentType: { error: false, message: '' },
                documentNumber: { error: false, message: '' },
                email: { error: false, message: '' },
                address: { error: false, message: '' },
                code: { error: false, message: '' },
                phoneNumber: { error: false, message: '' },
                birthdate: { error: false, message: '' },
                payRule: { error: false, message: '' },
                timeZone: { error: false, message: '' },
            });
            setInitialValues({
                first_name: '',
                last_name: '',
                document_type_id: '',
                document_employee: '',
                email: '',
                address: '',
                country_code: '',
                phone_number: '',
                birth_date: null,
                timezone: '',
            });
        }
    }, [openForm]);

    useEffect(() => {
        if (option !== 'update_employee') return;

        if (rowsSelected.length !== 1 || !canUpdateEmployee) {
            setOption(null);
            return;
        }

        setOpenForm(true);
        setOption(null);
    }, [option, rowsSelected.length, setOption, canUpdateEmployee]);

    return {
        t,
        title,
        canUpdateEmployee,
        openForm,
        setOpenForm,
        option,
        setOption,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        documentTypes,
        setDocumentTypes,
        documentType,
        setDocumentType,
        documentNumber,
        setDocumentNumber,
        email,
        setEmail,
        address,
        setAddress,
        codes,
        setCodes,
        code,
        setCode,
        phoneNumber,
        setPhoneNumber,
        birthdate,
        setBirthdate,
        timeZones,
        setTimeZones,
        timeZone,
        setTimeZone,
        validationErrors,
        setValidationErrors,
        searchDocumentType,
        setSearchDocumentType,
        searchTimeZone,
        setSearchTimeZone,
        searchCode,
        setSearchCode,
        loadingForm,
        setLoadingForm,
        loadingUpdateEmployee,
        setLoadingUpdateEmployee,
        checkInvalidData,
        hasChanges,
        showMessage,
        setShowMessage,
        updateEmployee,
        initialValues,
    };
};

export { useFormUpdateEmployee };
